import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { DataManagementService } from 'src/app/pages/data-management/data-management.service';
import { RealtimeWorkerService } from 'src/app/pages/realtime/realtime-worker.service';
import { MapService } from 'src/app/utils/leaflet/service/map.service';
import { StateService, ZoomState } from 'src/app/utils/leaflet/service/state.service';

@Component({
  selector: 'app-draggable-sheet',
  templateUrl: './draggable-sheet.component.html',
  styleUrls: ['./draggable-sheet.component.scss'],
})
export class DraggableSheetComponent implements OnInit, AfterViewInit {
  @Input() minHeight = 96;
  @Input() startExpanded = false;
  /** When true with startExpanded, open at full max height (not the mid 60% snap). */
  @Input() startMaximized = false;
  @Input() showClose = false;
  @Input() showBottomNav = false;
  @Input() historiqueActive = false;
  // When set, the close (X) button navigates to this route; otherwise it just collapses.
  @Input() closeRoute: string | null = null;
  @Output() closed = new EventEmitter<void>();

  readonly navBarHeight = 84;
  maxHeight = 500;
  height = 96;
  dragging = false;
  contentVisible = true;

  private pointerActive = false;
  private startY = 0;
  private startHeight = 0;

  constructor(
    private router: Router,
    public realTimeWorker: RealtimeWorkerService,
    public dataManagementService: DataManagementService,
    private mapService: MapService,
    private stateService: StateService,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  ngOnInit() {
    this.computeBounds();
    this.contentVisible = this.startExpanded || this.startMaximized;
    if (this.startMaximized) {
      this.height = this.maxHeight;
    } else if (this.startExpanded) {
      this.height = Math.round(this.maxHeight * 0.6);
    } else {
      this.height = this.collapsedHeight;
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.computeBounds();
      if (this.startMaximized) {
        this.expandMaximized();
      }
    }, 0);
  }

  /** Expand the pull-up to the maximum available height and show content. */
  expandMaximized(): void {
    this.computeBounds();
    this.contentVisible = true;
    this.height = this.maxHeight;
  }

  /** Force the sheet panel visible again (e.g. after video fullscreen). */
  showContent(): void {
    this.contentVisible = true;
    this.expandMaximized();
  }

  /**
   * After leaving video fullscreen / orientation changes, re-dock the sheet
   * to the bottom nav at full height (avoids a short “floating” mid-screen panel).
   */
  relayoutAfterFullscreen(): void {
    this.contentVisible = true;
    // Clear any leftover transforms from iOS fake-landscape overlays.
    const shell = this.elementRef.nativeElement.querySelector('.ds-shell') as HTMLElement | null;
    const sheet = this.elementRef.nativeElement.querySelector('.ds-sheet') as HTMLElement | null;
    for (const el of [shell, sheet, this.elementRef.nativeElement]) {
      if (!el) {
        continue;
      }
      el.style.transform = '';
      el.style.top = '';
      el.style.left = '';
      el.style.right = '';
      el.style.bottom = '';
      el.style.width = '';
      el.style.position = '';
    }
    if (sheet) {
      // Keep stacked sheet in normal flow above the bottom nav (never fixed mid-screen).
      sheet.style.position = 'relative';
      sheet.style.bottom = 'auto';
    }
    this.computeBounds();
    this.height = this.maxHeight;
    // Second pass after the browser settles portrait metrics.
    setTimeout(() => {
      this.computeBounds();
      this.contentVisible = true;
      this.height = this.maxHeight;
      if (sheet) {
        sheet.style.position = 'relative';
        sheet.style.bottom = 'auto';
      }
    }, 120);
    setTimeout(() => {
      this.computeBounds();
      this.height = this.maxHeight;
    }, 320);
  }

  /** Smooth-scroll the sheet body so `target` is visible near the top. */
  scrollToElement(target: HTMLElement | null | undefined, behavior: ScrollBehavior = 'smooth'): void {
    if (!target) {
      return;
    }
    this.expandMaximized();
    const content = this.elementRef.nativeElement.querySelector('.ds-content') as HTMLElement | null;
    const run = () => {
      if (!content) {
        target.scrollIntoView({ behavior, block: 'start' });
        return;
      }
      const contentRect = content.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const nextTop = content.scrollTop + (targetRect.top - contentRect.top) - 12;
      content.scrollTo({ top: Math.max(0, nextTop), behavior });
    };
    // Wait a frame so newly shown content (e.g. video panel) has layout.
    requestAnimationFrame(() => setTimeout(run, 50));
  }

  get collapsedHeight(): number {
    return this.minHeight;
  }

  get footerNavOffset(): number {
    return this.showBottomNav ? this.navBarHeight : 0;
  }

  @HostListener('window:resize')
  computeBounds() {
    const footerEl = document.querySelector('.ds-shell .ds-footer-nav .rt-bottom-nav') as HTMLElement | null;
    const measuredNav = footerEl?.offsetHeight ?? this.navBarHeight;
    const navTotal = this.showBottomNav ? measuredNav : 0;
    const available = window.innerHeight - navTotal;
    this.maxHeight = Math.max(
      Math.round(available * 0.88),
      this.minHeight + 120
    );
    if (this.height > this.maxHeight) {
      this.height = this.maxHeight;
    }
  }

  onDragStart(ev: MouseEvent | TouchEvent) {
    this.pointerActive = true;
    this.dragging = false;
    this.startY = this.getPointerY(ev);
    this.startHeight = this.height;
  }

  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  onDragMove(ev: MouseEvent | TouchEvent) {
    if (!this.pointerActive) {
      return;
    }
    const delta = this.startY - this.getPointerY(ev);
    if (Math.abs(delta) > 3) {
      this.dragging = true;
    }
    let height = this.startHeight + delta;
    height = Math.min(this.maxHeight, Math.max(this.collapsedHeight, height));
    this.height = height;
    // Keep historique data visible while dragging; only the diamond button collapses content.
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  onDragEnd() {
    if (!this.pointerActive) {
      return;
    }
    const wasTap = !this.dragging;
    this.pointerActive = false;
    setTimeout(() => (this.dragging = false), 0);
    // Tap on the grabber (no drag) closes the sheet — same as the X button.
    if (wasTap) {
      this.close();
    }
  }

  /** Click / keyboard on grabber — close unless this gesture was a drag. */
  onGrabberClick(ev: Event): void {
    if (this.dragging) {
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }
    // touchend already closed via onDragEnd; ignore duplicate click on touch devices.
    if ((ev as MouseEvent).detail === 0) {
      return;
    }
  }

  private getPointerY(ev: MouseEvent | TouchEvent): number {
    if (ev instanceof MouseEvent) {
      return ev.clientY;
    }
    const touch = ev.touches[0] || ev.changedTouches[0];
    return touch ? touch.clientY : 0;
  }

  close(ev?: Event) {
    if (ev) {
      ev.stopPropagation();
    }
    this.closed.emit();
    if (this.showBottomNav) {
      this.contentVisible = false;
      return;
    }
    if (this.closeRoute) {
      this.router.navigate([this.closeRoute]);
    } else {
      this.height = this.collapsedHeight;
    }
  }

  onCenterMenuClick(): void {
    if (!this.showBottomNav) {
      return;
    }
    this.contentVisible = !this.contentVisible;
    if (this.contentVisible) {
      this.height = Math.max(this.height, Math.round(this.maxHeight * 0.6));
    }
  }

  isVehicleFocused(): boolean {
    return this.realTimeWorker.trackingMode && !!this.dataManagementService.selectedVehicule;
  }

  getSelectedVehicleLabel(): string {
    const vehicule = this.dataManagementService.selectedVehicule;
    return vehicule ? this.dataManagementService.getVehiculeName(vehicule) : '';
  }

  clearVehicleSelection(): void {
    this.mapService.map?.closePopup();
    this.realTimeWorker.resetForGlobalView();
    this.stateService.setSelectedMarker(null);
    this.stateService.updateState(ZoomState.Default);
  }
}
